import React, { FC, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Container, Paper, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { skipToken } from '@reduxjs/toolkit/query';

import { useGetUserDocumentsQuery, useGetSSEEventsQuery } from '../healthWalletSlice';
import type { UserDocument } from '../healthWalletSlice';
import type { SectionProps } from './TabRenderer';
import { UploadDocumentButton } from '../UploadDocumentButton';
import { UserDocumentRow } from '../UserDocumentRow';
import { V1_ACCENT, V1_ACCENT_TINT, V1_ACCENT_BORDER } from './theme';
import { V1SearchField } from './V1SearchField';

// ============================================================================
// My Documents tab sections (V1 — NO design file; same adapt-directive as
// Notes, Leo 2026-07-13). The documents list is SMALL and fully loaded (one
// unpaginated query), so search + sort are CLIENT-SIDE — honest here, unlike
// the paginated Notes feed. Sort pills per Leo: date (Newest/Oldest) + A–Z /
// Z–A. Upload flow + row rendering + SSE keepalive reuse the existing
// building blocks (UploadDocumentButton / UserDocumentRow / shared SSE key).
// ============================================================================

const PAGE_WIDTH_SX = { maxWidth: { xs: '100%', sm: '94%' } };
// Shared SSE arg with the records-screen sync card (one EventSource per profile).
const SSE_KEY = 0;

type SortKey = 'newest' | 'oldest' | 'az' | 'za';

// --- 1. Hero -----------------------------------------------------------------
export const MyDocumentsHero: FC<SectionProps> = () => {
  const { data, isLoading } = useGetUserDocumentsQuery();
  const docs = data?.documents ?? [];
  if (isLoading) {
    return <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2, display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: V1_ACCENT }} /></Container>;
  }
  const processing = docs.filter((d) => d.status === 'pending' || d.status === 'processing').length;
  const completed = docs.filter((d) => d.status === 'completed').length;
  const tile = (value: number, label: string, bg: string, fg: string) => (
    <Box sx={{ width: { xs: 92, sm: 104 }, flexShrink: 0, bgcolor: bg, borderRadius: '15px', p: '16px 10px', textAlign: 'center' }}>
      <Typography sx={{ fontSize: { xs: 20, sm: 28 }, fontWeight: 700, color: fg, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ fontSize: 11, color: fg, mt: 0.75, fontWeight: 600 }}>{label}</Typography>
    </Box>
  );
  return (
    <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2 }}>
      <Paper elevation={0} sx={{ borderRadius: '20px', p: '24px 26px', border: `1px solid ${V1_ACCENT_BORDER}`, bgcolor: 'white', display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, color: V1_ACCENT, textTransform: 'uppercase', mb: 1.25 }}>
            Your documents at a glance
          </Typography>
          <Typography sx={{ fontSize: 21, fontWeight: 600, lineHeight: 1.4, letterSpacing: '-0.2px', maxWidth: 580 }}>
            You&rsquo;ve added <strong>{docs.length}</strong> document{docs.length === 1 ? '' : 's'} to your record.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {tile(docs.length, 'Documents', V1_ACCENT_TINT, V1_ACCENT)}
          {tile(completed, 'Processed', '#e6f6ec', '#1a7f3c')}
          {processing > 0 ? tile(processing, 'Processing', '#fdf0dd', '#9a5b00') : tile(0, 'Processing', '#f0f0f3', '#8e8e93')}
        </Box>
      </Paper>
    </Container>
  );
};

// --- 2. Explainer ---------------------------------------------------------------
export const MyDocumentsExplainer: FC<SectionProps> = () => (
  <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2 }}>
    <Box sx={{ bgcolor: V1_ACCENT_TINT, borderRadius: '16px', p: '16px 20px', display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
      <Box sx={{ flex: 'none', width: 26, height: 26, borderRadius: '50%', bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <InfoOutlinedIcon sx={{ color: V1_ACCENT, fontSize: 18 }} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 650, mb: 0.4 }}>About your documents</Typography>
        <Typography sx={{ fontSize: 13, color: '#4a4a52', lineHeight: 1.5, maxWidth: 820 }}>
          Documents you upload — photos, PDFs, or notes — are added to your health record. We read each one and file the
          health information it contains alongside the records from your providers. Uploading may take a moment to process.
        </Typography>
      </Box>
    </Box>
  </Container>
);

// --- 3. Body: search + sort pills + upload + the existing row renderer -----------
export const MyDocumentsBody: FC<SectionProps> = () => {
  const { data, isFetching, error, refetch } = useGetUserDocumentsQuery();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('newest');

  const documents = data?.documents ?? [];
  const hasProcessing = documents.some((d) => d.status === 'pending' || d.status === 'processing');
  // Keep the shared SSE connection alive while a doc is processing (central handler drives refetch).
  useGetSSEEventsQuery(hasProcessing ? SSE_KEY : skipToken);

  const q = search.trim().toLowerCase();
  const shown = useMemo(() => {
    const filtered = q ? documents.filter((d) => (d.document_name ?? '').toLowerCase().includes(q)) : [...documents];
    const byName = (a: UserDocument, b: UserDocument) => (a.document_name ?? '').localeCompare(b.document_name ?? '', undefined, { sensitivity: 'base' });
    const byDate = (a: UserDocument, b: UserDocument) => (a.received_at ?? '').localeCompare(b.received_at ?? '');
    switch (sort) {
      case 'oldest': return filtered.sort(byDate);
      case 'az': return filtered.sort(byName);
      case 'za': return filtered.sort((a, b) => byName(b, a));
      default: return filtered.sort((a, b) => byDate(b, a)); // newest
    }
  }, [documents, q, sort]);

  const pill = (key: SortKey, label: string) => (
    <Box key={key} onClick={() => setSort(key)} sx={{
      fontSize: 13, fontWeight: 600, px: 2, py: 1, borderRadius: '20px', cursor: 'pointer',
      bgcolor: sort === key ? V1_ACCENT : 'white', color: sort === key ? 'white' : '#3a3a3c',
      border: `1px solid ${sort === key ? V1_ACCENT : '#e0e0e6'}`,
    }}>{label}</Box>
  );

  return (
    <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2.5, pb: 12 }}>
      <V1SearchField value={search} onChange={setSearch} placeholder="Search your documents" />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        {pill('newest', 'Newest first')}
        {pill('oldest', 'Oldest first')}
        {pill('az', 'A–Z')}
        {pill('za', 'Z–A')}
        <Box sx={{ flex: 1 }} />
        <UploadDocumentButton onUploaded={refetch} />
      </Box>
      <Paper elevation={0} sx={{ borderRadius: '18px', border: `1px solid ${V1_ACCENT_BORDER}`, bgcolor: 'white', p: 2 }}>
        {isFetching && documents.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} sx={{ color: V1_ACCENT }} /></Box>
        ) : error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>Could not load your documents. Please try again.</Alert>
        ) : documents.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No documents yet — use the Upload button above to add an image, PDF, or note.
          </Typography>
        ) : shown.length === 0 ? (
          <Typography sx={{ p: 2, textAlign: 'center', color: '#8e8e93', fontSize: 14 }}>
            No documents match &ldquo;{search}&rdquo;.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {shown.map((doc) => (
              <UserDocumentRow key={doc.id} doc={doc} onChanged={refetch} defaultExpanded={false} />
            ))}
          </Box>
        )}
      </Paper>
      <Box sx={{ mt: 4.5, pt: 2.75, borderTop: '1px solid #e2e2e8', textAlign: 'center' }}>
        <Typography sx={{ fontSize: 12, color: '#aeaeb2', lineHeight: 1.55, maxWidth: 660, mx: 'auto' }}>
          Documents you upload become part of your health record. Remove one at any time to remove the information we
          extracted from it.
        </Typography>
      </Box>
    </Container>
  );
};
